import Api from 'utils/ApiRequest'
import API_URL from 'constants/api'
import { showSpinner, hideSpinner } from 'action/spinner'
import FileSaver from 'file-saver'
import Randomstring from 'randomstring'
import { setupNewEventSource } from './utils'
import { renderToastWarn } from 'utils/ToastUtils'

import {
  FETCH_REPORT_REQUEST,
  FETCH_REPORT_SUCCESS,
  FETCH_REPORT_ERROR,
} from 'actionTypes'

// report code for call controller after async report
const types = [
  API_URL.RUPTURE_REPORT_CSV, // 0
  API_URL.RUPTURE_REPORT_PDF, // 1
  API_URL.COMMERCIAL_MANAGEMENT_REPORT_CSV, // 2
  API_URL.COMMERCIAL_MANAGEMENT_REPORT_CSV, // 3
  API_URL.COMMERCIAL_MANAGEMENT_REPORT_PDF, // 4
  API_URL.LMEXPRESS_REPORT_CSV, // 5
  API_URL.CODE8OCCURRENCES_REPORT_CSV, // 6
  API_URL.CODE8OCCURRENCES_RECURRING_REPORT_CSV, // 7
  API_URL.SALES_COACHING_REPORT, // 8
  API_URL.ROTATIVE_INVENTORY_REPORT, // 9
  API_URL.TOTAL_ACCURACY_REPORT_CSV, // 10
  API_URL.KARDEX_REPORT, // 11
  API_URL.SECTION_ACCURACY_REPORT_CSV, // 12
  API_URL.HIGHER_ADJUSTMENTS_REPORT, // 13
  API_URL.ADJUSTMENT_VALUES_CSV, // 14
  API_URL.LINEAR_CSV, // 15
  API_URL.CODE8OCCURRENCES, // 16
  API_URL.FETCH_ADJUSTMENT_DONE_CSV, // 17
  API_URL.STOCK_TRANSFER_REPORT_CSV, // 18
  API_URL.ANALYTICS_REPORT, // 19
  API_URL.DELIMIT_REPORT_CSV, // 20
  API_URL.USAGE_CONSUMPTION_REPORT_CSV, // 21
  API_URL.BEEPING_REPORT_CSV, // 22
  API_URL.CODE8OCCURRENCES_REPORT_CSV_V2, // 23
  API_URL.CODE8RECURRENCES_RECURRING_REPORT_CSV_V2, // 24
  API_URL.ACCESS_MENUS_REPORT_STORES, // 25
  API_URL.ACCESS_MENUS_REPORT_LDAP, // 26
  API_URL.DATASHEET_MANAGEMENT_REPORT_AS_CSV, // 27,
  API_URL.CODE8RECURRENCES_SECTIONS_REPORT_CSV_V2, // 28
  API_URL.CODE8OCCURRENCE_STORES_REPORT_CSV, // 29
  API_URL.CODE8REVENUE_SECTIONS_REPORT_CSV, // 30
  API_URL.CODE8REVENUE_STORES_REPORT_CSV, // 31
  API_URL.ACCESS_MENUS_REPORT_LDAPS, // 32
  API_URL.ACCESS_MENUS_REPORT_MISSIONS, // 33
  API_URL.ACCESS_MENUS_REPORT_FILTER_LDAPS, // 34
  API_URL.ACCESS_MENUS_REPORT_FILTER_MISSIONS, // 35
  API_URL.REPORT_TABLE_CSV, // 36
  API_URL.TOTAL_ACCURACY_REPORT_CSV_V2, // 37
  API_URL.REPORT_GRAPH_STORES_CSV, // 38
  API_URL.REPORT_GRAPH_SECTIONS_CSV, // 39,
  API_URL.V2_COMMERCIAL_MANAGEMENT_REPORT_CSV, // 40,
  API_URL.V2_USE_CONSUME_REPORT, // 41,
  API_URL.SUPPLY_HISTORY_COUNT_CSV, // 42
  API_URL.SUPPLY_ACC_EFFEC_CSV, // 43
  API_URL.ZERO_COST_DPAC_ITEMS_REPORT_CSV, // 44
  API_URL.ROTATIVE_INVENTORY_FLUX_REPORT, // 45
  API_URL.LINEAR_FLUX_CSV, // 46
  API_URL.IRC_REPORT_FLUX_CSV, // 47
  API_URL.V2_ADJUSTMENT_VALUES_CSV, // 48
]

const REPORT_TYPE = {
  CSV: 0,
  PDF: 1,
  DEFAULT: 2,
  JASPER_CSV: 3,
}

/*
 * REQUEST REPORTS
 */
export const fetchAsyncReports =
  (token, values, filters, successCallback) => (dispatch) => {
    dispatch(fetchReportsRequest())

    const reportKey = Randomstring.generate()

    const query = {
      status: 200,
      reportKey,
      ...filters,
      hasPagination: false,
    }

    // -- Assync result --
    setupNewEventSource(
      reportKey,
      dispatch,
      (response) =>
        dispatch(fetchReportsSuccess(response, filters, successCallback)),
      (response) => dispatch(fetchReportsError(response))
    )

    const url = getUrl(values.tpReport)

    return new Api(token, dispatch)
    .post(url, query)
    .then(dispatch(showSpinner()))
  } 

/*
* REQUEST FLUX REPORT
*/

export const fetchFluxReport = (token, values, filters, t, signalController = new AbortController(), isJasper = false) => (dispatch) => {
  dispatch(fetchReportsRequest())
  const query = {
    status: 200,
    ...filters,
    hasPagination: false,
  }
  
  const onClose = () => dispatch(hideSpinner())

  const onError = (error) => {
    if (t) renderToastWarn(t('report.generation.error'))
    dispatch(fetchReportsError(error))
    dispatch(hideSpinner())
  }

  const onMessage = (response) => {
    response.type = REPORT_TYPE.CSV
    if(isJasper) response.type = REPORT_TYPE.JASPER_CSV
    dispatch(fetchReportsSuccess(response))
    onClose()
  }

  const url = getUrl(values.tpReport)

  const abort = () => {
    signalController.abort()
    onClose()
  }

  dispatch(showSpinner("", true, abort))
  return new Api(token, dispatch)
  .eventSourceStream(url, query, null, signalController, onMessage, onClose, onError)
  .catch((error) => {
    onError(error)
  })
}

/*
 * REQUEST REPORTS
 */
export const fetchReports = (token, values, filters, t) => (dispatch) => {
  dispatch(fetchReportsRequest())

  const query = {
    status: 200,
    ...filters,
    hasPagination: false,
  }

  const url = getUrl(values.tpReport)

  return new Api(token, dispatch)
    .post(url, query)
    .then(dispatch(showSpinner()))
    .then((response) => {
      dispatch(fetchReportsSuccess(response))
      dispatch(hideSpinner())
    })
    .catch((error) => {
      if (t) renderToastWarn(t('report.generation.error'))
      dispatch(fetchReportsError(error))
      dispatch(hideSpinner())
    })
}

export const fetchReportsRequest = () => ({
  type: FETCH_REPORT_REQUEST,
})

export const fetchReportsSuccess = (response, filters, successCallback) => {
  let blob
  if (response.type === REPORT_TYPE.CSV) {
    blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
    FileSaver.saveAs(blob, 'report.csv')
  } else if (response.type === REPORT_TYPE.PDF) {
    const data = new Blob([b64toBlob(response.data, 'application/pdf')], {
      type: 'application/pdf',
    })
    FileSaver.saveAs(data, 'report')
  } else if (response.type === REPORT_TYPE.DEFAULT) {
    return successCallback(JSON.parse(response.data), filters)
  } else if (response.type === REPORT_TYPE.JASPER_CSV) {
    blob = new Blob([b64toBlob(response.data, 'text/csv;charset=utf-8')], {
      type: 'text/csv;charset=utf-8',
    })
    FileSaver.saveAs(blob, response.fileName ? response.fileName : 'report.csv')
  } else {
    blob = new Blob([response], { type: 'application/pdf' })
    const objectUrl = URL.createObjectURL(blob)
    window.open(objectUrl)
  }

  return {
    type: FETCH_REPORT_SUCCESS,
    payload: response.data,
  }
}

export const fetchReportsError = (error) => ({
  type: FETCH_REPORT_ERROR,
  payload: error,
})

const getUrl = (value) => types[value]

function b64toBlob(b64Data, contentType, sliceSize) {
  contentType = contentType || ''
  sliceSize = sliceSize || 512

  const byteCharacters = atob(b64Data)
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)

    byteArrays.push(byteArray)
  }

  const blob = new Blob(byteArrays, { type: contentType })
  return blob
}
